// ink-component
import React, { useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import shortid from 'shortid';

import Input from '../Input/Input';
import Loader from '../Loader/Loader';
import DeprecatedDropdownItem from '../../deprecated/DeprecatedDropdown/DeprecatedDropdownItem/DeprecatedDropdownItem';

import { bemHelper, strip } from '../../helpers';
import useInput from '../../hooks/useInput';
import useClickOutside from '../../hooks/useClickOutside';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';

import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../helpers/constants/global-props';

import TableContext from '../Table/TableContext/TableContext';

type Options = Option[];

type MetaType = {
  initial?: string | number;
  isInvalid?: boolean;
};

type InputType = {
  value?: string | number;
  onFocus?: (option: Option) => void;
  onChange?: (option: Option) => void;
  onBlur?: (option: Option) => void;
};

export type FilterBy = string[] | ((value: string | number, options: Option[]) => ReactNode);

export type Option = {
  label?: string | number;
  value?: string | number;
  hasTopDivider?: boolean;
  isHeader?: boolean;
};

export interface TypeaheadProps {
  /** Unique identifier */
  id?: string;
  /** External control and initial value */
  value?: string | number;
  /** Array of options to be rendered. The `label` is used for both showing in the Input
   * and the default front end filter. The `value` is the actual value that will be sent.
   * Both `label` and `value` are "required", but you can change their names. For example, if
   * instead of `value` your object have an `id` you can use the prop `valueKey` as `id`. The
   * same applies to the label field.
   * `hasTopDivider` and `isHeader` are CSS modifiers that can be injected in specific options.
   * You can pass any object format that you need to the `options` props, as long as you treat them in the `render` function prop */
  options?: Option[] | any[];
  /** Specify which option key to use for display.
   * By default, the selector will use the `label` key. */
  labelKey?: string;
  /** Specify which option key to use for return.
   * By default, the selector will use the `value` key. */
  valueKey?: string;
  /** An array of fields in option to search. You can also pass your own callback to take
   * complete control over how the filtering works. Note that, the case sensitivity and ignore
   * diacritics behaviors will be ignored in this case, since you are now completely
   * overriding the algorithm. The function sends back the options, and the input value. */
  filterBy?: FilterBy;
  /** Delay for the filter function to be called */
  filterDelay?: number;
  /** Indicates that the Typeahead component will accept input value not found in provided options. This
   * prop only works with the default behavior of `filterBy`. */
  allowCustomOption?: boolean;
  /** Callback for option selection. Sends back the option and the event */
  onOptionSelect?: (option?: Option, event?: Event) => void;
  /** Callback for key presses. Sends back the event */
  onKeyDown?: (event: KeyboardEvent) => void;
  /** Callback called when the value of the Input changes. Sends back the new value. */
  onChange?: (value: string | number) => void;
  /** Placeholder */
  placeholder?: string;
  /** Message displayed in the menu when there are no valid results */
  emptyLabel?: ReactNode;
  /** Defines Typeahead input size */
  size?: 'full' | 'large' | 'half' | 'quarter';
  /** Defines if the Dropdown closes on option selection */
  closeOnSelect?: boolean;
  /** RenderProp for the options. Receives an option and should return a node. */
  render?: (option: any) => ReactNode;
  /** Defines if input is inside a table */
  isCell?: boolean;
  /** Defines if input is inline */
  isInline?: boolean;
  /** Defines if input is invalid */
  isInvalid?: boolean;
  /** Defines if input is disabled */
  isDisabled?: boolean;
  /** Defines if Dropdown is loading. Used mostly for async behavior */
  isLoading?: boolean;
  /** Defines if is right aligned */
  alignRight?: boolean;
  /** HTML maxLength */
  maxLength?: number;
  /** Clear the Input on select */
  clearOnSelect?: boolean;
  /** HTML type */
  autoFocus?: boolean;
  /** Set [autoComplete](/patterns/forms/#autocomplete) */
  autoComplete?: string;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  dataPrivate?: string | boolean;
  /** @ignore */
  defaultValue?: string | number;
  /** @ignore */
  meta?: MetaType;
  /** @ignore */
  input?: InputType;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @aka search
 * @categories form
 * @purpose Search and select an option among matching results
 * @deprecated This component is deprecated and replaced by [NewTypeahead](https://ink.carta.com/components/NewTypeahead/)
 * @status deprecated
 * @replacementComponent NewTypeahead
 * @dateOfDeletion December 1st, 2023
 */
function Typeahead({
  input = {},
  meta = {},
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...props
}: TypeaheadProps) {
  useDeprecatedWarning({ componentName: 'Typeahead', substitute: 'NewTypeahead' });

  // @ts-ignore
  const initialValue: string | number = input.value || props.value || meta.initial || props.defaultValue;
  const selectedOption: Option | undefined = options.find(opt => strip(opt[valueKey]) === strip(initialValue));
  const name = useMemo(() => `${props.id}-${shortid.generate()}`, [props.id]);
  const [optionsState, setOptions] = useState<Options>(selectedOption ? [selectedOption] : options);
  const [label, setLabel] = useState<string | number>(selectedOption ? selectedOption[labelKey] : '');

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const closeDropdown = (): void => setOpen(false);
  const getSelectedOption = (): Option | undefined => optionsState.find(opt => strip(opt[labelKey]) === strip(label));

  const onClickOutside = (): void => {
    if (input.onBlur) {
      const selectedOption = getSelectedOption();
      input.onBlur(selectedOption && selectedOption[valueKey]);
    }
    closeDropdown();
  };
  const [open, setOpen, ref] = useClickOutside(onClickOutside);

  const filterBy: FilterBy = props.filterBy ? props.filterBy : [labelKey];

  const setExternalLabel = (options: Options, value: string | number) => {
    const selectedOption: Option | undefined = options.find(opt => strip(opt[valueKey]) === strip(value));
    setLabel(selectedOption && selectedOption[labelKey]);
  };

  const { tableId } = useContext(TableContext);
  const [value, setValue] = useInput(initialValue);

  useDeepCompareEffect(() => {
    if (options) {
      setOptions(options);
    }
  }, [options]);

  useDidUpdateEffect(() => {
    if (!isNil(props.value)) setExternalLabel(options, props.value);
    if (!isNil(input.value)) setExternalLabel(options, input.value);
  }, [props.value, input.value]);

  useEffect(() => {
    /* Typeaheads should close whenever the Table they are inside scrolls horizontally. */
    if (tableId && open) {
      const table = document.getElementById(tableId);
      const scrollWrapper = table && table.closest('.wrapper-ink-table--scroll');
      // Adds event listener to close in the case of a horizontal scroll (with a delay)
      if (scrollWrapper)
        scrollWrapper.addEventListener(
          'scroll',
          debounce(() => setOpen(false), 500),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, open]);

  const openDropdown = (): void => setOpen(true);

  const handleFocus = (): void => {
    if (input.onFocus) {
      const selectedOption = getSelectedOption();
      input.onFocus(selectedOption && selectedOption[valueKey]);
    }
    openDropdown();
  };

  const defineClassName = (): string => {
    const b = bemHelper();

    return b('wrapper-dropdown', {
      right: props.alignRight,
      control: props.isCell,
      full: props.size === 'full',
    });
  };

  const defineDropdownClassName = (): string => {
    const b = bemHelper();

    return b('ink-dropdown', {
      open,
      typeahead: true,
      [`${props.size}`]: Boolean(props.size) && props.size !== 'large',
    });
  };

  const toggleDropdown = (): void => setOpen(isOpen => !isOpen);

  const defaultFilterBy = (value: string | number, options: Option[]) =>
    options.filter(
      opt =>
        Array.isArray(filterBy) && filterBy.some(key => String(strip(opt[key] || '')).includes(String(strip(value)))),
    );

  const formatInput = (): void => {
    const selectedOption = getSelectedOption();
    if (selectedOption) {
      setLabel(selectedOption[labelKey]);
      setValue(selectedOption[valueKey]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFilterBy = useCallback(
    debounce(
      (value: string | number, options: Option[]) => typeof filterBy === 'function' && filterBy(value, options),
      props.filterDelay,
    ),
    [props.filterDelay],
  );

  const handleChange = (label: string | number): void => {
    props.onChange && props.onChange(label);
    if (!open) {
      openDropdown();
    }

    if (typeof filterBy === 'function') {
      debounceFilterBy(label, options);
    } else if (props.allowCustomOption && defaultFilterBy(label, options).length === 0) {
      setOptions([{ [labelKey]: label, value: label }]);
    } else {
      setOptions(defaultFilterBy(label, options));
    }

    const selectedOption: Option = options.find(opt => strip(opt[labelKey]) === strip(label));

    setValue(selectedOption && selectedOption[valueKey]);
    setLabel(label);
  };

  const handleOptionClick = (option: Option, event: Event) => {
    if (props.clearOnSelect) {
      setLabel('');
      setValue('');
    } else {
      handleChange(option[labelKey]);
    }
    if (props.closeOnSelect) {
      closeDropdown();
    }
    if (input.onChange) {
      input.onChange(option[valueKey]);
    }
    if (props.onOptionSelect) {
      props.onOptionSelect(option, event);
    }
  };

  const goToNextOption = (options: Option[], value?: string | number) => {
    const valueIdx = options.findIndex(opt => value === opt[labelKey]);
    let nextOption;

    if (value && options[valueIdx + 1]) {
      nextOption = options[valueIdx + 1];
    } else if (options[0]) {
      [nextOption] = options;
    }

    if (nextOption.isHeader) {
      const optionsLength = options.length;
      if (optionsLength <= valueIdx + 2) {
        nextOption = options.find(opt => !opt.isHeader);
      } else {
        nextOption = options[valueIdx + 2];
      }
    }

    const nextValue = nextOption ? nextOption[labelKey] : value;

    setValue(nextValue);
    setLabel(nextValue);
  };

  const goToPreviousOption = (options: Option[], value: string | number) => {
    const valueIdx = options.findIndex(opt => value === opt[labelKey]);
    let previousOption;

    if (options[valueIdx - 1]) {
      previousOption = options[valueIdx - 1];
    } else if (options[options.length - 1]) {
      previousOption = options[options.length - 1];
    }

    if (previousOption.isHeader) {
      previousOption = options[valueIdx - 2];
      if (previousOption === undefined) {
        const reversed = options.slice().reverse();
        previousOption = reversed.find(opt => !opt.isHeader);
      }
    }

    const previousValue = previousOption ? previousOption[labelKey] : value;

    setValue(previousValue);
    setLabel(previousValue);
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openDropdown();
      } else {
        goToNextOption(optionsState, value);
      }
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (value && open) {
        goToPreviousOption(optionsState, value);
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (props.clearOnSelect) {
        setLabel('');
        setValue('');
      } else {
        handleChange(label);
      }
      if (props.onOptionSelect) {
        props.onOptionSelect(getSelectedOption && getSelectedOption(), event);
      }
      if (input.onChange) {
        const selectedOption = getSelectedOption();
        input.onChange(selectedOption && selectedOption[valueKey]);
      }
      toggleDropdown();
    }
    if (event.key === 'Tab') {
      closeDropdown();
      formatInput();
    }
    props.onKeyDown && props.onKeyDown(event);
  };

  const renderOptions = (): ReactNode => {
    return optionsState.map((option, index) => (
      <DeprecatedDropdownItem
        key={index}
        hasTopDivider={option.hasTopDivider}
        isHeader={option.isHeader}
        isActive={strip(option[labelKey]) === strip(value)}
        onClick={e => handleOptionClick(option, e)}
      >
        {props.render ? props.render(option) : option[labelKey]}
      </DeprecatedDropdownItem>
    ));
  };

  return (
    <div
      ref={ref}
      className={defineClassName()}
      data-private={props.dataPrivate}
      data-testid={dataTestId}
      {...globalProps}
    >
      <Input
        id={props.id}
        value={label}
        name={name}
        aria-label={ariaLabel}
        defaultValue={props.defaultValue}
        placeholder={props.placeholder}
        size={props.size}
        isCell={props.isCell}
        isDisabled={props.isDisabled}
        isInline={props.isInline}
        isInvalid={props.isInvalid}
        maxLength={props.maxLength}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoFocus={props.autoFocus}
        autoComplete={props.autoComplete}
      />
      <div className={defineDropdownClassName()}>
        <Loader isLoading={props.isLoading} />
        {!props.isLoading && (
          <ul>
            {renderOptions()}
            {Boolean(!optionsState.length && props.emptyLabel && !props.allowCustomOption) && (
              <DeprecatedDropdownItem isDisabled>{props.emptyLabel}</DeprecatedDropdownItem>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

Typeahead.defaultProps = {
  id: 'typeahead',
  value: '',
  options: [],
  labelKey: 'label',
  valueKey: 'value',
  filterBy: undefined,
  filterDelay: 0,
  allowCustomOption: false,
  onOptionSelect: undefined,
  onKeyDown: () => {},
  onChange: () => {},
  defaultValue: '',
  placeholder: ' ',
  emptyLabel: 'No matches found.',
  size: 'large',
  closeOnSelect: true,
  render: undefined,
  isCell: false,
  isInline: false,
  isInvalid: false,
  isDisabled: false,
  isLoading: false,
  alignRight: false,
  maxLength: undefined,
  clearOnSelect: false,
  autoFocus: false,
  autoComplete: 'off',
};

Typeahead.displayName = 'Typeahead';

export default Typeahead;
